import TiptapTableHeader from "@tiptap/extension-table-header";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { getCellsInRow, isColumnSelected, parseBackgroundColorFromHTML, selectColumn } from "./utils";

/** Default shading for header cells — Tailwind neutral-100, matching the rest of the editor chrome. */
export const kDefaultHeaderBackgroundColor = "#f5f5f5";

export const TableHeader = TiptapTableHeader.extend({
  // No nested tables or Page Breaks in a table cell — pdf-report-core can't render a nested
  // table, and a Page Break here breaks the enclosing table rather than the page (DMS-2004).
  // Matches TableCell's own restriction (see Cell.ts) — the base extension's "block+" default
  // allows any block-level node.
  content: "(paragraph | heading | bulletList | orderedList | imageBlock)+",

  addAttributes() {
    return {
      colspan: {
        default: 1,
      },
      rowspan: {
        default: 1,
      },
      colwidth: {
        default: null,
        parseHTML: (element) => {
          const colwidth = element.getAttribute("colwidth");
          const value = colwidth ? colwidth.split(",").map((item) => parseInt(item, 10)) : null;

          return value;
        },
      },
      backgroundColor: {
        default: kDefaultHeaderBackgroundColor,
        parseHTML: (element) => parseBackgroundColorFromHTML(element as HTMLElement),
        renderHTML: (attributes) =>
          attributes.backgroundColor ? { style: `background-color: ${attributes.backgroundColor}` } : {},
      },
    };
  },

  addProseMirrorPlugins() {
    const { isEditable } = this.editor;

    return [
      new Plugin({
        props: {
          decorations: (state) => {
            if (!isEditable) {
              return DecorationSet.empty;
            }

            const { doc, selection } = state;
            const decorations: Decoration[] = [];
            const cells = getCellsInRow(0)(selection);

            if (cells) {
              cells.forEach(({ pos }: { pos: number }, index: number) => {
                decorations.push(
                  Decoration.widget(pos + 1, () => {
                    const colSelected = isColumnSelected(index)(selection);
                    let className = "grip-column";

                    if (colSelected) {
                      className += " selected";
                    }

                    if (index === 0) {
                      className += " first";
                    }

                    if (index === cells.length - 1) {
                      className += " last";
                    }

                    const grip = document.createElement("a");

                    grip.className = className;
                    grip.addEventListener("mousedown", (event) => {
                      event.preventDefault();
                      event.stopImmediatePropagation();

                      this.editor.view.dispatch(selectColumn(index)(this.editor.state.tr));
                    });

                    return grip;
                  })
                );
              });
            }

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
      // An empty header cell has no text to carry a bold mark — marks only ever apply to actual
      // text content, so "the header row is bold" can't be baked into an empty paragraph. Instead,
      // this arms the cursor's stored marks with bold whenever it's resting in an empty textblock
      // inside a tableHeader cell (fresh table insert, Tab between header cells, clicking into
      // one, ...), so the *next* character the user types comes out bold without them having to
      // reach for the bold toggle themselves. They can still turn it back off per the usual
      // toggleBold command — this only sets the default.
      new Plugin({
        key: new PluginKey("tableHeaderBoldStoredMarks"),
        appendTransaction(transactions, _oldState, newState) {
          if (!isEditable) return null;
          if (!transactions.some((tr) => tr.selectionSet || tr.docChanged)) return null;

          const { selection, schema } = newState;
          if (!selection.empty) return null;

          const boldType = schema.marks.bold;
          if (!boldType) return null;

          const $pos = selection.$from;
          if (!$pos.parent.isTextblock || $pos.parent.content.size > 0) return null;

          const inHeaderCell = Array.from({ length: $pos.depth }, (_, i) => $pos.node(i + 1)).some(
            (node) => node.type.name === "tableHeader"
          );
          if (!inHeaderCell) return null;

          const activeMarks = newState.storedMarks ?? $pos.marks();
          if (boldType.isInSet(activeMarks)) return null;

          return newState.tr.setStoredMarks(boldType.create().addToSet(activeMarks));
        },
      }),
    ];
  },
});

export default TableHeader;
